<template>
  <section class="hit-calculator">
    <div class="simulation-progress"></div>
    <readonly-alert v-if="!authorized" />
    <saved-scenario-alert
      v-if="savedScenario"
      v-bind:title="savedScenario.title"
    />
    <header class="page-header">
      <h1>Hit calculator</h1>
    </header>
    <h4>How many opponents are you facing?</h4>
    <num-opponents-selector
      v-bind:numOpponents="state.players.numPlayers"
      v-bind:authorized="authorized"
      v-on:addOpponent="addPlayer()"
      v-on:removeOpponent="removePlayer()"
    />
    <h4>What are your hole cards?</h4>
    <hole-cards-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:popoverDirectionClass="'dropdown'"
      v-bind:disabled="!authorized"
      v-on:setPresetHoleCard="setPresetHoleCard($event)"
      v-on:removePresetHoleCard="removePresetHoleCard($event)"
    />
    <h4>What are the board cards?</h4>
    <preset-community-cards-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:popoverDirectionClass="'dropdown'"
      v-bind:disabled="!authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event)"
      v-on:setBoard="setBoard($event)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
    />
    <h3>Preflop</h3>
    <range-for-street
      v-for="(player, playerIndex) in state.players._players"
      v-bind:key="`preflop-${player.id}`"
      v-bind:ref='`preflop-${playerIndex}`'
      v-bind:player="player"
      v-bind:playerIndex="playerIndex"
      v-bind:playerStateIndex="0"
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:storeModuleName="'hitCalculator'"
      v-bind:authorized="authorized"
    >
      <section
        v-if="(state.presetHoleCards.bothSet && !state.players.hasEmptyRange('preflop'))"
        slot="left-header"
        class="equity-results-container"
      >
        <equity-results
          v-bind:equity="player.states[0].equity"
          v-bind:equityString="player.states[0].equityString"
          v-bind:winPercentageString="player.states[0].winPercentageString"
          v-bind:tiePercentageString="player.states[0].tiePercentageString"
          v-bind:hasSimulationBeenRun="ableToGetPreflopEquities()"
        />
        <span
          data-title="This shows the equity that this range has against the selected hole cards in step two, and against any other preflop ranges if you're facing multiple opponents."
          data-toggle="tooltip"
          data-placement="top"
          class="glyphicon glyphicon-question-sign"
        ></span>
      </section>
    </range-for-street>
    <h3>Flop</h3>
    <flop-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:popoverDirectionClass="'dropdown'"
      v-bind:disabled="!authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event)"
      v-on:setBoard="setBoard($event)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
    />
    <cumulative-hit-percentages
      v-if="state.players.numPlayers > 1"
      v-bind:cumulativeHitPercentages="cumulativeHitPercentages"
      v-bind:moreThanTwoPlayers="state.players.numPlayers > 2"
      v-bind:streetsToDisplay="['flop']"
    />
    <range-for-street
      v-for="(player, playerIndex) in state.players._players"
      v-bind:key="`flop-${player.id}`"
      v-bind:ref='`flop-${playerIndex}`'
      v-bind:player="player"
      v-bind:playerIndex="playerIndex"
      v-bind:playerStateIndex="1"
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(1)"
      v-bind:storeModuleName="'hitCalculator'"
      v-bind:simulateButton="showNonexhaustiveSimulationButton(playerIndex, 'flop')"
      v-bind:authorized="authorized"
      v-on:simulate="runNonexhaustiveFiveCardHandPercentagesSimulationsForFlop(playerIndex)"
    >
      <section
        v-if="(state.presetHoleCards.bothSet && state.presetCommunityCards.flopSet && !state.players.hasEmptyRange('flop'))"
        slot="left-header"
        class="equity-results-container"
      >
        <equity-results
          v-bind:equity="player.states[1].equity"
          v-bind:equityString="player.states[1].equityString"
          v-bind:winPercentageString="player.states[1].winPercentageString"
          v-bind:tiePercentageString="player.states[1].tiePercentageString"
          v-bind:hasSimulationBeenRun="ableToGetFlopEquities()"
        />
        <span
          data-title="This shows the equity that this range has against the selected hole cards in step two, and against any other flop ranges if you're facing multiple opponents. It assumes that the flop will always be what you have it set as, but that the turn and river will be dealt randomly."
          data-toggle="tooltip"
          data-placement="top"
          class="glyphicon glyphicon-question-sign"
        ></span>
      </section>
      <hit-calculator-insights
        v-if="state.presetCommunityCards.numSet <= 3"
        slot="insights"
        v-bind:hitPercentage="player.states[1].hitPercentage"
        v-bind:hitPercentageUpToDate="isHitPercentageUpToDate[playerIndex][0]"
      />
    </range-for-street>
    <h3>Turn</h3>
    <turn-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:disabled="!authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
    />
    <cumulative-hit-percentages
      v-if="state.players.numPlayers > 1"
      v-bind:cumulativeHitPercentages="cumulativeHitPercentages"
      v-bind:moreThanTwoPlayers="state.players.numPlayers > 2"
      v-bind:streetsToDisplay="['turn']"
    />
    <range-for-street
      v-for="(player, playerIndex) in state.players._players"
      v-bind:key="`turn-${player.id}`"
      v-bind:ref='`turn-${playerIndex}`'
      v-bind:player="player"
      v-bind:playerIndex="playerIndex"
      v-bind:playerStateIndex="2"
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(2)"
      v-bind:storeModuleName="'hitCalculator'"
      v-bind:simulateButton="showNonexhaustiveSimulationButton(playerIndex, 'turn')"
      v-bind:authorized="authorized"
      v-on:simulate="runNonexhaustiveFiveCardHandPercentagesSimulationsForTurn(playerIndex)"
    >
      <section
        v-if="(state.presetHoleCards.bothSet && state.presetCommunityCards.turnSet && !state.players.hasEmptyRange('turn'))"
        slot="left-header"
        class="equity-results-container"
      >
        <equity-results
          v-bind:equity="player.states[2].equity"
          v-bind:equityString="player.states[2].equityString"
          v-bind:winPercentageString="player.states[2].winPercentageString"
          v-bind:tiePercentageString="player.states[2].tiePercentageString"
          v-bind:hasSimulationBeenRun="ableToGetTurnEquities()"
        />
        <span
          data-title="This shows the equity that this range has against the selected hole cards in step two, and against any other turn ranges if you're facing multiple opponents. It assumes that the turn will always be what you have it set as, but that the river will be dealt randomly."
          data-toggle="tooltip"
          data-placement="top"
          class="glyphicon glyphicon-question-sign"
        ></span>
      </section>
      <hit-calculator-insights
        v-if="state.presetCommunityCards.numSet === 4"
        slot="insights"
        v-bind:hitPercentage="player.states[2].hitPercentage"
        v-bind:hitPercentageUpToDate="isHitPercentageUpToDate[playerIndex][1]"
      />
    </range-for-street>
    <h3>River</h3>
    <river-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:disabled="!authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
    />
    <cumulative-hit-percentages
      v-if="state.players.numPlayers > 1"
      v-bind:cumulativeHitPercentages="cumulativeHitPercentages"
      v-bind:moreThanTwoPlayers="state.players.numPlayers > 2"
      v-bind:streetsToDisplay="['river']"
    />
    <range-for-street
      v-for="(player, playerIndex) in state.players._players"
      v-bind:key="`river-${player.id}`"
      v-bind:ref='`river-${playerIndex}`'
      v-bind:player="player"
      v-bind:playerIndex="playerIndex"
      v-bind:playerStateIndex="3"
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(3)"
      v-bind:storeModuleName="'hitCalculator'"
      v-bind:simulateButton="showNonexhaustiveSimulationButton(playerIndex, 'river')"
      v-bind:authorized="authorized"
      v-on:simulate="runNonexhaustiveFiveCardHandPercentagesSimulationsForRiver(playerIndex)"
    >
      <section
        v-if="(state.presetHoleCards.bothSet && state.presetCommunityCards.riverSet && !state.players.hasEmptyRange('river'))"
        slot="left-header"
        class="equity-results-container"
      >
        <equity-results
          v-bind:equity="player.states[3].equity"
          v-bind:equityString="player.states[3].equityString"
          v-bind:winPercentageString="player.states[3].winPercentageString"
          v-bind:tiePercentageString="player.states[3].tiePercentageString"
          v-bind:hasSimulationBeenRun="ableToGetRiverEquities()"
        />
        <span
          data-title="This shows the equity that this range has against the selected hole cards in step two, and against any other river ranges if you're facing multiple opponents."
          data-toggle="tooltip"
          data-placement="top"
          class="glyphicon glyphicon-question-sign"
        ></span>
      </section>
      <hit-calculator-insights
        v-if="state.presetCommunityCards.numSet === 5"
        slot="insights"
        v-bind:hitPercentage="player.states[3].hitPercentage"
        v-bind:hitPercentageUpToDate="isHitPercentageUpToDate[playerIndex][2]"
      />
    </range-for-street>
    <br>
    <create-sharable-url-button
      v-bind:disabled="!authorized"
      v-on:createSharableUrl="$emit('createSharableUrl', {
        method: 'createSharableHitCalculatorURL',
        state: state,
        approximateWaitTime: 5,
      })"
    />
    <save-scenario-button
      v-bind:disabled="!authorized"
      v-bind:savedScenario="savedScenario"
      v-on:saveScenario="$emit('saveScenario', {
        method: 'createSharableHitCalculatorURL',
        savedScenarioPropertyName: 'hitCalculator',
        state: state,
      })"
      v-on:updateScenario="$emit('updateScenario', {
        method: '_constructHitCalculatorData',
        state: state,
      })"
    />
    <onboarding-modal
      toolName="hit calculator"
      urlPath="hit-calculator"
    />
    <help-icon path="/help/hit-calculator" />
  </section>
</template>

<script>
import ReadonlyAlert from '@/components/utilities/readonly-alert';
import SavedScenarioAlert from '@/components/utilities/saved-scenario-alert';
import NumOpponentsSelector from '@/components/guided-analyses/num-opponents-selector';
import HoleCardsSelector from '@/components/controls/card-selection/hole-cards-selector';
import PresetCommunityCardSelector from '@/components/controls/card-selection/preset-community-cards-selector';
import RangeForStreet from '@/components/utilities/range-for-street';
import EquityResults from '@/components/hand-range/equity-results';
import HitCalculatorInsights from '@/components/hit-calculator/five-card-hands-section/insights/insights';
import FlopSelector from '@/components/controls/card-selection/flop-selector';
import TurnSelector from '@/components/controls/card-selection/turn-selector';
import RiverSelector from '@/components/controls/card-selection/river-selector';
import CumulativeHitPercentages from '@/components/cumulative-hit-percentages';
import CreateSharableUrlButton from '@/components/controls/create-sharable-url-button';
import SaveScenarioButton from '@/components/controls/save-scenario-button';
import OnboardingModal from '@/components/onboarding-modal';
import HelpIcon from '@/components/help-icon';
import simulator from '@/services/simulator/simulator';
import validator from '@/services/simulator/validator';
import utilities from '@/services/utilities/utilities';
import _ from 'lodash';

export default {
  name: 'hit-calculator',
  components: {
    'readonly-alert': ReadonlyAlert,
    'saved-scenario-alert': SavedScenarioAlert,
    'num-opponents-selector': NumOpponentsSelector,
    'hole-cards-selector': HoleCardsSelector,
    'preset-community-cards-selector': PresetCommunityCardSelector,
    'range-for-street': RangeForStreet,
    'equity-results': EquityResults,
    'hit-calculator-insights': HitCalculatorInsights,
    'flop-selector': FlopSelector,
    'turn-selector': TurnSelector,
    'river-selector': RiverSelector,
    'cumulative-hit-percentages': CumulativeHitPercentages,
    'create-sharable-url-button': CreateSharableUrlButton,
    'save-scenario-button': SaveScenarioButton,
    'onboarding-modal': OnboardingModal,
    'help-icon': HelpIcon,
  },
  data: function () {
    return {
      canRunUnauthorizedSimulation: true,
    };
  },
  computed: {
    state: function () {
      return this.$store.state.hitCalculator;
    },
    savedScenario: function () {
      let routeId = this.$route.params.id;

      if (!this.authorized) {
        return false;
      }

      return this.$store.state.account.user.savedScenarios.hitCalculator.find(function (savedScenario) {
        return savedScenario.url.includes(routeId);
      });
    },
    multiplePlayers: function () {
      return this.state.players.numPlayers > 1;
    },
    insightsVisible: function () {
      return this.$store.state.account.user.settings.hitCalculator.insightsVisible;
    },
    isHitPercentageUpToDate: function () {
      let results = [];

      this.state.players.forEach(function (player) {
        results.push([
          player.states[1].isHitPercentageUpToDate(player, this.state.presetCommunityCards, this.state.presetHoleCards),
          player.states[2].isHitPercentageUpToDate(player, this.state.presetCommunityCards, this.state.presetHoleCards),
          player.states[3].isHitPercentageUpToDate(player, this.state.presetCommunityCards, this.state.presetHoleCards)
        ]);
      }, this);

      return results;
    },
    cumulativeHitPercentages: function () {
      return this.$store.getters['hitCalculator/cumulativeHitPercentages'];
    },
    authorized: function () {
      return this.$store.getters['account/authorizedForBasic'];
    },
  },
  mounted: function () {
    if (this.$route.params.id) {
      this.load();
    }

    else {
      this.$store.commit('hitCalculator/setFiveCardHandValuesToDefaults', {
        settings: this.$store.state.account.user.settings,
        opponentTightness: 'straightforward',
      });
    }

    this.handleOnboard();

    // five card hand simulations
    this.$watch(
      this.flopExhaustiveFiveCardHandSimulationsWatcher,
      _.debounce(this.runExhaustiveFiveCardHandPercentagesSimulationsForFlop, 1000)
    );
    this.$watch(
      this.turnExhaustiveFiveCardHandSimulationsWatcher,
      _.debounce(this.runExhaustiveFiveCardHandPercentagesSimulationsForTurn, 1000)
    );
    this.$watch(
      this.riverExhaustiveFiveCardHandSimulationsWatcher,
      _.debounce(this.runExhaustiveFiveCardHandPercentagesSimulationsForRiver, 1000)
    );

    // equity simulations
    this.$watch(
      this.preflopEquitySimulationsWatcher,
      _.debounce(this.setPreflopEquities, 1000)
    );
    this.$watch(
      this.flopEquitySimulationsWatcher,
      _.debounce(this.setFlopEquities, 1000)
    );
    this.$watch(
      this.turnEquitySimulationsWatcher,
      _.debounce(this.setTurnEquities, 1000)
    );
    this.$watch(
      this.riverEquitySimulationsWatcher,
      _.debounce(this.setRiverEquities, 1000)
    );
  },
  methods: {
    // mounted
    load: function () {
      let self = this;

      self.$http.get(`${process.env.BASE_API_URL}/sharable-url-store/` + self.$route.params.id)
        .then(function (response) {
          if (!response || !response.data.storeState) {
            alert('Didn\'t get data back from server. Redirecting to the default hit calculator page.');
            self.$router.push('/hit-calculator');
          } else {
            self.$store.commit('hitCalculator/setState', response.data.storeState);
            self.simulate();
          }
        })
        .catch(function () {
          alert('URL doesn\'t exist. Redirecting to the default hit calculator page.');
          self.$router.push('/hit-calculator');
        })
      ;
    },
    handleOnboard: function () {
      let self = this;

      if (
        !self.authorized ||
        !self.$store.state.account.user.settings.onboarding ||
        self.$store.state.account.user.settings.onboarding.hitCalculator
      ) {
        return;
      }

      $(self.$el).find('.onboarding-modal').modal();
      self.$http
        .patch(`${process.env.BASE_API_URL}/user/onboard`, { page: 'hitCalculator' })
        .then(function () {
          self.$store.commit('account/onboard', 'hitCalculator');
        })
        .catch(function () {
          console.log('Error: Unable to onboard.');
        })
      ;
    },

    // controls
    addPlayer: function () {
      this.$store.commit('hitCalculator/addPlayer', {
        settings: this.$store.state.account.user.settings,
        opponentTightness: 'straightforward',
      });
    },
    removePlayer: function () {
      this.$store.commit('hitCalculator/removePlayer', this.state.players.numPlayers - 1);
      this.simulate();
    },
    setPresetHoleCard: function (params) {
      this.$store.commit('hitCalculator/removePresetHoleCard', params.position);
      this.$store.commit('hitCalculator/setPresetHoleCard', {
        card: params.card,
        position: params.position
      });
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
      this.resetAllDoubleSliders();
    },
    removePresetHoleCard: function (position) {
      this.$store.commit('hitCalculator/removePresetHoleCard', position);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
      this.resetAllDoubleSliders();
    },
    setIndividualPresetCommunityCard: function (params) {
      this.$store.commit('hitCalculator/removePresetCommunityCard', params.position);
      this.$store.commit('hitCalculator/setIndividualPresetCommunityCard', {
        card: params.card,
        position: params.position
      });
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
      this.resetAllDoubleSliders();
    },
    setBoard: function (boardCards) {
      this.$store.commit('hitCalculator/removePresetCommunityCard', 'flop1');
      this.$store.commit('hitCalculator/removePresetCommunityCard', 'flop2');
      this.$store.commit('hitCalculator/removePresetCommunityCard', 'flop3');
      this.$store.commit('hitCalculator/removePresetCommunityCard', 'turn');
      this.$store.commit('hitCalculator/removePresetCommunityCard', 'river');
      this.$store.commit('hitCalculator/setBoard', boardCards);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
      this.resetAllDoubleSliders();
    },
    removePresetCommunityCard: function (position) {
      this.$store.commit('hitCalculator/removePresetCommunityCard', position);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
      this.resetAllDoubleSliders();
    },

    // player
    setRangesAccordingToFiltersForPlayer: function (playerIndex) {
      this.$store.commit('hitCalculator/setRangesAccordingToFilters', playerIndex);
    },
    setRangesAccordingToFiltersForAllPlayers: function () {
      this.state.players.forEach(function (player, playerIndex) {
        this.setRangesAccordingToFiltersForPlayer(playerIndex)
      }, this);
    },
    resetDoubleSliderForPlayer: function (playerIndex) {
      if (this.$refs[`preflop-${playerIndex}`]) {
        this.$refs[`preflop-${playerIndex}`][0].resetDoubleSlider(playerIndex);
      }
    },
    resetAllDoubleSliders: function () {
      this.state.players.forEach(function (player, index) {
        this.resetDoubleSliderForPlayer(index);
      }, this);
    },

    // simulation
    flopExhaustiveFiveCardHandSimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[0].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },
    turnExhaustiveFiveCardHandSimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[1].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },
    riverExhaustiveFiveCardHandSimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[2].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },

    preflopEquitySimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[0].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },
    flopEquitySimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[1].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },
    turnEquitySimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[2].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },
    riverEquitySimulationsWatcher: function () {
      let sum = 0;

      this.state.players.forEach(function (player) {
        sum += player.states[3].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    },

    simulate: function () {
      let self = this;

      self
        .setPreflopEquities()
        .then(self.setFlopEquities)
        .then(self.setTurnEquities)
        .then(self.setRiverEquities)
        .then(self.runExhaustiveFiveCardHandPercentagesSimulationsForFlop)
        .then(self.runExhaustiveFiveCardHandPercentagesSimulationsForTurn)
        .then(self.runExhaustiveFiveCardHandPercentagesSimulationsForRiver)
        .then(function () {
          self.canRunUnauthorizedSimulation = false;
        })
      ;
    },

    ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheFlop: function () {
      let returnValue = true;

      // not sure if commenting this out introduces a bug or not, but I don't think it does
      // if (this.state.players.numPlayers > 1) {
      //   returnValue = false;
      // }

      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        returnValue = false;
      }

      if (!this.state.presetCommunityCards.flopSet) {
        returnValue = false;
      }

      if (this.state.players.hasEmptyRange('preflop')) {
        returnValue = false;
      }

      return returnValue;
    },
    ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheTurn: function () {
      let returnValue = true;

      // not sure if commenting this out introduces a bug or not, but I don't think it does
      // if (this.state.players.numPlayers > 1) {
      //   returnValue = false;
      // }

      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        returnValue = false;
      }

      if (!this.state.presetCommunityCards.turnSet) {
        returnValue = false;
      }

      if (this.state.players.hasEmptyRange('flop')) {
        returnValue = false;
      }

      return returnValue;
    },
    ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheRiver: function () {
      let returnValue = true;

      // not sure if commenting this out introduces a bug or not, but I don't think it does
      // if (this.state.players.numPlayers > 1) {
      //   returnValue = false;
      // }

      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        returnValue = false;
      }

      if (!this.state.presetCommunityCards.riverSet) {
        returnValue = false;
      }

      if (this.state.players.hasEmptyRange('turn')) {
        returnValue = false;
      }

      return returnValue;
    },
    runExhaustiveFiveCardHandPercentagesSimulationsForFlop: function () {
      let self = this;

      if (!self.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheFlop()) {
        return Promise.resolve();
      }

      return simulator
        .getFlopFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('hitCalculator/setLastSimulationInfo', 1);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    runExhaustiveFiveCardHandPercentagesSimulationsForTurn: function () {
      let self = this;

      if (!self.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheTurn()) {
        return Promise.resolve();
      }

      return simulator
        .getTurnFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('hitCalculator/setLastSimulationInfo', 2);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    runExhaustiveFiveCardHandPercentagesSimulationsForRiver: function () {
      let self = this;

      if (!self.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheRiver()) {
        return Promise.resolve();
      }

      return simulator
        .getRiverFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('hitCalculator/setLastSimulationInfo', 3);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },

    showNonexhaustiveSimulationButton: function (playerIndex, street) {
      if (!this.authorized) {
        return false;
      }

      if (!this.ableToRunNonexhaustiveFiveCardHandPercentageSimulationsForPlayer(playerIndex)) {
        return false;
      } else if (street === 'flop' && this.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheFlop()) {
        return false;
      } else if (street === 'turn' && this.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheTurn()) {
        return false;
      } else if (street === 'river' && this.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheRiver()) {
        return false;
      }

      return true;
    },
    ableToRunNonexhaustiveFiveCardHandPercentageSimulationsForPlayer: function (playerIndex) {
      return !this.state.players.find(playerIndex).hasEmptyRange('preflop');
    },
    runNonexhaustiveFiveCardHandPercentagesSimulationsForFlop: function (playerIndex) {
      return this._runNonexhaustiveFiveCardHandPercentagesSimulationsForStreet(playerIndex, 'flop', 1);
    },
    runNonexhaustiveFiveCardHandPercentagesSimulationsForTurn: function (playerIndex) {
      return this._runNonexhaustiveFiveCardHandPercentagesSimulationsForStreet(playerIndex, 'turn', 2);
    },
    runNonexhaustiveFiveCardHandPercentagesSimulationsForRiver: function (playerIndex) {
      return this._runNonexhaustiveFiveCardHandPercentagesSimulationsForStreet(playerIndex, 'river', 3);
    },
    _runNonexhaustiveFiveCardHandPercentagesSimulationsForStreet: function (playerIndex, streetName, streetIndex) {
      let self = this;

      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        return;
      }

      return self.__runNonexhaustiveSimulationsForPlayer(playerIndex, streetName, streetIndex)
        .then(function () {
          self.setRangesAccordingToFiltersForPlayer(playerIndex);
          self.$store.commit('hitCalculator/setLastSimulationInfo', streetIndex);
          self.$store.commit('hitCalculator/setHitPercentageBeingComputed', {
            playerIndex: playerIndex,
            playerStateIndex: streetIndex,
            value: true,
          });
          self.$store.dispatch('hitCalculator/setHitPercentage', {
            playerIndex: playerIndex,
            playerStateIndex: streetIndex,
          });
          self.$store.commit('hitCalculator/clearMatchingSpecificHands', {
            playerIndex: playerIndex,
            playerStateIndex: streetIndex,
          });
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    __runNonexhaustiveSimulationsForPlayer: function (playerIndex, streetName, streetIndex) {
      let self = this;
      let player = self.state.players.find(playerIndex);
      let simulatorMethod = self.___getNonexhaustiveSimulatorMethod(streetName);
      let numberOfSimulations = self.$store.state.account.user.settings.hitCalculator.numberOfSimulations;

      return simulator[simulatorMethod](
        player.getWeightedRangeToUseForSimulation(self.state.presetCommunityCards, streetName),
        self.state.presetCommunityCards.getArrayForStateViaStreet(streetName),
        numberOfSimulations,
        function (value) {
          self.incrementSimulationProgress(value, playerIndex, streetName);
        }
      )
        .then(function (handAndDrawInfo) {
          self.$store.commit('hitCalculator/setHandAndDrawInfo', {
            playerIndex: playerIndex,
            playerStateIndex: streetIndex,
            handAndDrawInfoFromSimulationResults: handAndDrawInfo,
          });
          self.$store.commit('hitCalculator/setProbabilitiesOfFiveCardHands', {
            playerIndex: playerIndex,
            playerStateIndex: streetIndex,
            handAndDrawInfo: handAndDrawInfo,
            numberOfSimulations: numberOfSimulations,
          });
          self.resetSimulationProgress(playerIndex, streetName);
        })
      ;
    },
    ___getNonexhaustiveSimulatorMethod: function (streetName) {
      let capitalizedStreetName = _.capitalize(streetName);
      let simulatorMethod = `get${capitalizedStreetName}FiveCardHandPercentagesViaSimulations`;

      return simulatorMethod;
    },

    ableToGetPreflopEquities: function () {
      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        return false;
      }

      return (
        this.state.presetHoleCards.bothSet &&
        !this.state.players.hasEmptyRange('preflop')
      );
    },
    ableToGetFlopEquities: function () {
      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        return false;
      }

      return (
        this.state.presetHoleCards.bothSet &&
        this.state.presetCommunityCards.flopSet &&
        !this.state.players.hasEmptyRange('flop')
      );
    },
    ableToGetTurnEquities: function () {
      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        return false;
      }

      return (
        this.state.presetHoleCards.bothSet &&
        this.state.presetCommunityCards.turnSet &&
        !this.state.players.hasEmptyRange('turn')
      );
    },
    ableToGetRiverEquities: function () {
      if (!this.authorized && !this.canRunUnauthorizedSimulation) {
        return false;
      }

      return (
        this.state.presetHoleCards.bothSet &&
        this.state.presetCommunityCards.riverSet &&
        !this.state.players.hasEmptyRange('river')
      );
    },
    setPreflopEquities: function () {
      if (!this.ableToGetPreflopEquities()) {
        return Promise.resolve();
      }

      return this._setEquitiesForStreet(0);
    },
    setFlopEquities: function () {
      if (!this.ableToGetFlopEquities()) {
        return Promise.resolve();
      }

      return this._setEquitiesForStreet(1);
    },
    setTurnEquities: function () {
      if (!this.ableToGetTurnEquities()) {
        return Promise.resolve();
      }

      return this._setEquitiesForStreet(2);
    },
    setRiverEquities: function () {
      if (!this.ableToGetRiverEquities()) {
        return Promise.resolve();
      }

      return this._setEquitiesForStreet(3);
    },
    _setEquitiesForStreet: function (playerStateIndex) {
      let self = this;
      let weightedRanges = self.__getWeightedRanges(playerStateIndex);
      let numberOfSimulations = self.$store.state.account.user.settings.equityCalculator.numberOfSimulations;
      let presetCommunityCardsArray = self.state.presetCommunityCards.getArrayForStateViaIndex(playerStateIndex);

      return simulator
        .getEquities(weightedRanges, presetCommunityCardsArray, numberOfSimulations, function () {})
        .then(function (simulationResults) {
          self.$store.commit('hitCalculator/updateEquityResults', {
            playerStateIndex: playerStateIndex,
            simulationResults: simulationResults,
            numberOfSimulations: numberOfSimulations,
            rakePercentage: self.$store.state.account.user.settings.equityCalculator.rakeAndTip.rakePercentage,
            tipPercentage: self.$store.state.account.user.settings.equityCalculator.rakeAndTip.tipPercentage
          });

          if (process.env.NODE_ENV !== 'testing') {
            $(self.$el).find('[data-toggle="tooltip"]').tooltip();
          }
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the equity simulations.');
        })
      ;
    },
    __getWeightedRanges: function (playerStateIndex) {
      let weightedRanges = [];
      let heroWeightedRange = [
        [this.state.presetHoleCards.holeCardOne, this.state.presetHoleCards.holeCardTwo]
      ];

      weightedRanges.push(heroWeightedRange);
      this.state.players.forEach(function (player) {
        weightedRanges.push(player.states[playerStateIndex].generalHands.weightedRange);
      }, this);

      return weightedRanges;
    },

    getTextWhenFiveCardHandPercentagesArentUpToDate: function (playerStateIndex) {
      if (
        (playerStateIndex === 1 && this.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheFlop()) ||
        (playerStateIndex === 2 && this.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheTurn()) ||
        (playerStateIndex === 3 && this.ableToRunExhaustiveFiveCardHandPercentageSimulationsForTheRiver())
      ) {
        return 'Running simulations...';
      } else {
        return 'Not up to date. Run simulations to update.';
      }
    },
    incrementSimulationProgress: function (value, playerIndex, streetName) {
      let $simulationProgress = $(this.$el).find('.simulation-progress');
      let $simulationButtonContainer = $(this.$refs[`${streetName}-${playerIndex}`][0].$el).find('.simulate-button-container');
      let $simulationButton = $simulationButtonContainer.find('.simulate-button');
      let $simulationButtonProgressBox = $simulationButtonContainer.find('.progress-box');

      $simulationProgress.css('width', value + '%');
      $simulationButton.prop('disabled', true);
      $simulationButtonProgressBox.css('z-index', '1');
      $simulationButtonProgressBox.css('width', value + '%');
      $simulationButtonProgressBox.css('cursor', 'not-allowed');
    },
    resetSimulationProgress: function (playerIndex, streetName) {
      let $simulationProgress = $(this.$el).find('.simulation-progress');
      let $simulationButtonContainer = $(this.$refs[`${streetName}-${playerIndex}`][0].$el).find('.simulate-button-container');
      let $simulationButton = $simulationButtonContainer.find('.simulate-button');
      let $simulationButtonText = $simulationButtonContainer.find('.text');
      let $simulationButtonProgressBox = $simulationButtonContainer.find('.progress-box');

      $simulationProgress.css('width', '0%');
      $simulationButton.prop('disabled', false);
      $simulationButtonText.text('Simulate');
      $simulationButtonProgressBox.css('z-index', '-1');
      $simulationButtonProgressBox.css('cursor', 'auto');
    },
  },
};
</script>

<style lang="scss" scoped>
  .simulation-progress {
    position: fixed;
    top: 0;
    left: 0;
    height: 5px;
    background: #5cb85c6e;
    z-index: 1;
  }

  .equity-results-container {
    display: inline-block;

    .glyphicon-question-sign {
      cursor: pointer;
      font-size: 14px;
      vertical-align: 4px;
      color: gray;
    }
  }

  .save-scenario-button-container {
    margin-top: 10px;
  }

  @media (max-width: 991px) {
    .flop-selector, .turn-selector, .river-selector {
      margin-bottom: 15px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/hit-calculator/hit-calculator.vue